import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Chapter, Reveal } from "@/components/motion";
import { IMAGES, SERVICES } from "@/data/content";

export function PageHero({ eyebrow, title, subtitle, image, testid }) {
  return (
    <section className="relative flex min-h-[52svh] items-end overflow-hidden bg-[#1C201D] pt-32 pb-16" data-testid={testid}>
      <img src={image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C201D] via-[#1C201D]/50 to-[#1C201D]/40" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow !text-[#C5A059]">{eyebrow}</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.1] text-[#FAF7F2] max-w-3xl">
            {title}
          </h1>
          {subtitle && <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#E8ECE8]">{subtitle}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Landscape Design & Construction Services | Luxury Backyard Design Calgary"
        description="Complete landscape design and construction, new-build landscaping, acreage development, outdoor kitchens, natural stone, pools and fire features for Calgary, Springbank and Bearspaw estates."
      />
      <PageHero
        testid="services-hero"
        eyebrow="Our Services"
        title="One Considered Plan for the Entire Estate"
        subtitle="From first sketch to final planting, our in-house team designs and builds every element of your outdoor environment."
        image={IMAGES.patioFireplace}
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {SERVICES.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-detail-${i + 1}`}
              className={`grid items-center gap-8 lg:gap-16 border-b border-[#E0D8CC] py-14 lg:grid-cols-12 ${i === 0 ? "pt-0" : ""}`}
            >
              <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                {s.image ? (
                  <div className="relative">
                    <div className="absolute -inset-3 border border-[#C5A059]/25" aria-hidden="true" />
                    <img src={s.image} alt={s.title} loading="lazy" className="relative h-[300px] sm:h-[360px] w-full object-cover" />
                  </div>
                ) : (
                  <div className="flex h-[300px] sm:h-[360px] items-center justify-center bg-[#F2EDE4]">
                    <span className="font-display text-8xl font-light italic text-[#C5A059]/50">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                )}
              </Reveal>
              <Reveal delay={0.1} className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <span className="font-display italic text-lg text-[#8B5A2B]">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="mt-2 font-display text-3xl sm:text-4xl font-light tracking-tight leading-[1.15]">{s.title}</h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-[#4A524C]">{s.desc}</p>
                <Link to="/quote" data-testid={`service-quote-link-${i + 1}`} className="mt-7 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#8B5A2B] hover:text-[#C5A059] transition-colors">
                  Discuss This Service <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
          ))}

          <Reveal className="pt-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Not Sure Where to Begin?</h2>
            <p className="mx-auto mt-4 max-w-lg text-base text-[#4A524C]">
              Tell us about your property and vision — we'll shape the right scope together.
            </p>
            <Link to="/quote" data-testid="services-quote-btn" className="btn-primary mt-8">
              Request a Private Quote <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
